import React, { useState, useRef } from 'react';
import api, { handleAPIError } from '../../services/api';

interface FreshnessRow {
  _url: string;
  _effectiveAge: number;
  _freshnessCategory: string;
  _priorityScore: number;
  _action: string;
  _reason: string;
  _clicks: number;
  _impressions: number;
  _backlinks: number;
  _daysSinceModified: number;
}

interface FreshnessResponse {
  totalRows: number;
  freshnessCounts: { Fresh: number; Aging: number; Stale: number; Critical: number };
  actionCounts: Record<string, number>;
  avgScore: number;
  scoreBuckets: Record<string, number>;
  rows: FreshnessRow[];
}

const FRESHNESS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  Fresh: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  Aging: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
  Stale: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
  Critical: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
};

function truncateUrl(url: string, maxLen = 60): string {
  if (url.length <= maxLen) return url;
  return url.slice(0, maxLen - 3) + '...';
}

const ContentFreshness: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<FreshnessResponse | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
    } else {
      setFileName(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const csvData = await file.text();
      const response = await api.post<FreshnessResponse>('/seotools/content-freshness', { csvData });
      const sorted = [...response.data.rows].sort((a, b) => b._priorityScore - a._priorityScore);
      setData({ ...response.data, rows: sorted });
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const freshnessCategories = ['Fresh', 'Aging', 'Stale', 'Critical'] as const;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Content Freshness</h2>
        <p className="text-sm text-gray-500 mt-1">
          Upload a CSV with your content data to analyze freshness and get prioritized update recommendations.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex gap-3 items-end">
        <div className="flex-1">
          <label htmlFor="csv-upload" className="block text-sm font-medium text-gray-700 mb-1">
            CSV File
          </label>
          <div className="flex items-center gap-3">
            <input
              id="csv-upload"
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 file:cursor-pointer"
              disabled={loading}
            />
          </div>
          {fileName && (
            <p className="text-xs text-gray-500 mt-1">Selected: {fileName}</p>
          )}
        </div>
        <button
          type="submit"
          disabled={loading || !fileName}
          className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Analyzing...
            </span>
          ) : (
            'Analyze'
          )}
        </button>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Rows</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{data.totalRows}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Avg Score</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{data.avgScore.toFixed(1)}</p>
            </div>
            <div className="col-span-2 bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Freshness Distribution</p>
              <div className="flex flex-wrap gap-2">
                {freshnessCategories.map((cat) => {
                  const count = data.freshnessCounts[cat] || 0;
                  const colors = FRESHNESS_COLORS[cat];
                  return (
                    <span
                      key={cat}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${colors.bg} ${colors.text} ${colors.border}`}
                    >
                      {cat}: {count}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Action Counts */}
          {data.actionCounts && Object.keys(data.actionCounts).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Recommended Actions</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.actionCounts).map(([action, count]) => (
                  <span
                    key={action}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 border border-gray-200"
                  >
                    {action}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Results Table */}
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Freshness</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Modified</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.rows.map((row, idx) => {
                  const colors = FRESHNESS_COLORS[row._freshnessCategory] || FRESHNESS_COLORS.Stale;
                  return (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono" title={row._url}>
                        {truncateUrl(row._url)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800 font-semibold">
                        {row._priorityScore.toFixed(1)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                          {row._freshnessCategory}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row._action}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row._clicks}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row._impressions}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row._daysSinceModified}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ContentFreshness;
